import clsx from "clsx";
import { Dayjs } from "dayjs";
import {
  KeyboardEvent,
  MouseEventHandler,
  ReactElement,
  useEffect,
  useState,
} from "react";

import { SVG_MUI } from "@ryde/assets";
import { useRtl } from "@ryde/redux";
import {
  getDayjsFromTimeString,
  isValidTimeString,
  KeyboardKeys,
  normalizeToArrayValue,
  parseTimeString,
  TimeZoneEnum,
  TTimeHHmm,
  useFormats,
  ValueType,
} from "@ryde/shared";

import { AdornmentLensButton, LocalizationProvider } from "../../../common";
import {
  TimeFieldInputStyled,
  TimeFieldLabel,
  TimeFieldWrapper,
} from "../time.styled";

interface ITimeFilterFieldProps {
  fieldId: string;
  label: string;
  /** The stored filter value, scalar or single-element array; anything that is not a time renders blank. */
  value: ValueType | null | undefined;
  /** Called on blur, Enter and the lens button. `undefined` means "no valid time typed" — clear the filter. */
  onApply: (time: TTimeHHmm | undefined) => void;
  onRemove: () => void;
}

/**
 * The "type a time" filter chip: label, an hh:mm field, a lens to apply and an X to remove. Owns the field
 * state and the canonicalisation to HH:mm; the caller owns where the value is stored and how it is labelled,
 * so the grid (custom field id) and the reports bar (template key) share one field.
 *
 * The field is pinned to UTC because this is a bare time of day, not an instant.
 */
export const TimeFilterField = ({
  fieldId,
  label,
  value,
  onApply,
  onRemove,
}: ITimeFilterFieldProps): ReactElement => {
  const { isRtl } = useRtl();
  const { formatTimeMainFormatNormalized, timeFormat } = useFormats();
  const [isFocused, setIsFocused] = useState(false);
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);

  useEffect(() => {
    const stringValue = String(normalizeToArrayValue(value ?? []).at(0) || "");

    if (isValidTimeString(stringValue)) {
      setSelectedTime(getDayjsFromTimeString(stringValue, TimeZoneEnum.UTC));
    } else {
      setSelectedTime(null);
    }
  }, [value]);

  const filterApply = (): void => {
    const timeStringToApply = selectedTime?.format(timeFormat) || "";
    const parsedTime = parseTimeString(timeStringToApply, timeFormat);

    // formatTimeMainFormatNormalized is typed as a loose string; it is only reached once the value has been
    // validated as a time, so it is always the HH:mm main format here.
    onApply(
      isValidTimeString(parsedTime)
        ? (formatTimeMainFormatNormalized(parsedTime) as TTimeHHmm)
        : undefined,
    );
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== KeyboardKeys.ENTER) {
      return;
    }

    filterApply();
  };

  const onBlur = (): void => {
    filterApply();
    setIsFocused(false);
  };

  const handleTimeChange = (newTime: Dayjs | null): void => {
    setSelectedTime(newTime);
  };

  const removeFilter: MouseEventHandler = e => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <LocalizationProvider>
      <TimeFieldWrapper
        className={clsx({
          "TimeFieldWrapper-focused": isFocused,
        })}
      >
        <TimeFieldLabel htmlFor={fieldId}>{label}</TimeFieldLabel>
        <TimeFieldInputStyled
          $isRtl={isRtl}
          name={fieldId}
          onFocus={() => setIsFocused(true)}
          onBlur={onBlur}
          timezone={TimeZoneEnum.UTC}
          value={selectedTime}
          onChange={handleTimeChange}
          format={timeFormat}
          slotProps={{
            textField: {
              id: fieldId,
              slotProps: {
                input: {
                  endAdornment: (
                    <AdornmentLensButton position="end" onClick={filterApply} />
                  ),
                },
              },
            },
          }}
          onKeyDown={onKeyDown}
        />
        <SVG_MUI.CircleXMark
          className="circle-x-mark"
          onClick={e => removeFilter(e)}
        />
      </TimeFieldWrapper>
    </LocalizationProvider>
  );
};
